/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: Prop types for checking props type.
import Moment from "moment"; // Npm: Moment.js library for date and time formatting.
import * as XLSX from "xlsx"; // Npm: XLSX library.
import _ from "underscore"; // Npm: utility module.
import { toast } from "react-hot-toast"; // Npm: React toastify for notifications.
import { HiPlusCircle } from "react-icons/hi2"; // Npm: React icons.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { useMutation, useQuery } from "@apollo/client"; // Npm: Apollo client for GraphQL queries and mutations.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI DataGrid component.
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * SIBLINGS
 */
import TableSpinner from "components/TableSpinner";
import DownloadToExcel from "components/DownloadToExcel";
import Modal from "components/Modal";
import RatePlanUpsert from "components/RatePlanUpsert";
import RatePlanDelete from "components/RatePlanDelete";
import FileUpload from "components/FileUpload";
import RatePlanDropDownOptions from "components/RatePlanDropDownOptions";
import PlanFilterOptions from "components/PlanFilterOptions";
import { MemoizedInput } from "components/MemoizedInput";

/*
 * GRAPHS
 */
import RatePlanReadQuery from "./__query__/index.ratePlan.read.query";
import RatePlanCreateMutation from "./__mutation__/index.ratePlan.create.mutation";

/*
 * STYLES
 */
import { buttonStyle } from "./index.style";

/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Local variable.
  let _rows;

  // Const assignment.
  const _successFlags =
    Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
      (i) => i.key,
    );

  // Hook assignment.
  const [selectAll, setSelectAll] = React.useState(false);
  const [onlyShowSenderIdBasedRouting, setOnlyShowSenderIdBasedRouting] =
    React.useState(false);
  const [onlyShowPhoneNumberBasedRouting, setOnlyShowPhoneNumberBasedRouting] =
    React.useState(false);
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });

  // Debounced search states.
  const [mccSearch, setMccSearchQuery] = React.useState("");
  const [mncSearch, setMncSearchQuery] = React.useState("");
  const [countrySearch, setCountrySearchQuery] = React.useState("");
  const [operatorSearch, setOperatorSearchQuery] = React.useState("");

  const [MutationRatePlanCreate] = useMutation(RatePlanCreateMutation);
  const {
    isOpen: isRatePlanCreateOpen,
    onOpen: onRatePlanCreateOpen,
    onClose: onRatePlanCreateClose,
  } = useDisclosure();
  const {
    isOpen: isRatePlanUpdateOpen,
    onOpen: onRatePlanUpdateOpen,
    onClose: onRatePlanUpdateClose,
  } = useDisclosure();
  const {
    isOpen: isRatePlanDeleteOpen,
    onOpen: onRatePlanDeleteOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  const _QueryRatePlanRead = useQuery(RatePlanReadQuery, {
    variables: {
      rateId: passOn?.rateId,
      mccNameToSearch: mccSearch,
      mncNameToSearch: mncSearch,
      countryNameToSearch: countrySearch,
      operatorNameToSearch: operatorSearch,
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
    },
    fetchPolicy: Object.React.App.fetchPolicy,
    pollInterval: Object.React.App.pollInterval,
  });

  const _isFirstLoadCompleted = React.useRef(false);
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Debounce handlers.
  const _onMccInputChange = React.useRef(
    _.debounce((e) => setMccSearchQuery(e.target.value), 800),
  ).current;
  const _onMncInputChange = React.useRef(
    _.debounce((e) => setMncSearchQuery(e.target.value), 800),
  ).current;
  const _onCountryInputChange = React.useRef(
    _.debounce((e) => setCountrySearchQuery(e.target.value), 800),
  ).current;
  const _onOperatorsInputChange = React.useRef(
    _.debounce((e) => setOperatorSearchQuery(e.target.value), 800),
  ).current;

  // Cleanup on unmount.
  React.useEffect(() => {
    return () => {
      _onMccInputChange.cancel?.();
      _onMncInputChange.cancel?.();
      _onCountryInputChange.cancel?.();
      _onOperatorsInputChange.cancel?.();
    };
  }, []);

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched.
    if (_QueryRatePlanRead.data) {
      // Reset states.
      setSelectedIds([]);
      setSelectAll(false);
    }
  }, [
    isRatePlanCreateOpen,
    isRatePlanUpdateOpen,
    isRatePlanDeleteOpen,
    _QueryRatePlanRead.data,
  ]);

  // Object assignment.
  const _HandleCheckboxChange = (id) => {
    // Check if id is already selected.
    if (selectedIds.includes(id)) {
      // Remove id from selected ids.
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      // Add id to selected ids.
      setSelectedIds([...selectedIds, id]);
    }
  };

  const _HandleSelectAll = () => {
    // Set select all to opposite of current value.
    setSelectAll(!selectAll);
    setSelectedIds(
      selectAll
        ? []
        : _QueryRatePlanRead?.data?.RatePlanRead?.map((item) => item.id) || [],
    );
  };

  // Check if first load is completed.
  if (
    !_QueryRatePlanRead.loading &&
    0 < _QueryRatePlanRead.data?.RatePlanRead?.length
  ) {
    _isFirstLoadCompleted.current = true;
  }

  if (
    0 === _QueryRatePlanRead.data?.RatePlanRead?.length ||
    (0 < _QueryRatePlanRead.data?.RatePlanRead?.length &&
      !_.every(_.pluck(_QueryRatePlanRead.data?.RatePlanRead, "status"), (i) =>
        _successFlags?.includes(i),
      ))
  ) {
    _isFirstLoadCompleted.current = false;
  }

  // Filter based on senderId or phoneNumber.
  if (onlyShowSenderIdBasedRouting) {
    // Filter rows.
    _rows = _QueryRatePlanRead.data?.RatePlanRead?.filter(
      (i) => !_.isEmpty(i?.SenderIdDirectory?.displayName),
    );
  } else if (onlyShowPhoneNumberBasedRouting) {
    // Filter rows.
    _rows = _QueryRatePlanRead.data?.RatePlanRead?.filter(
      (i) => !_.isEmpty(i?.PhoneNumberoDirectory?.displayName),
    );
  } else {
    // Filter rows.
    _rows = _QueryRatePlanRead.data?.RatePlanRead;
  }

  // Columns definition.
  const columns = React.useMemo(
    () => [
      {
        field: "checkbox",
        headerName: "",
        width: 70,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        renderHeader: () => (
          <Checkbox
            bg="gray.300"
            onChange={_HandleSelectAll}
            isChecked={selectAll}
          />
        ),
        renderCell: ({ row }) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Checkbox
              bg="gray.300"
              isChecked={selectedIds.includes(row.id)}
              onChange={() => _HandleCheckboxChange(row.id)}
            />
          </Box>
        ),
      },
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 100,
        sortable: false,
        filterable: false,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        hide: true,
      },
      {
        field: "rateName",
        headerName: "Rate Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "mcc",
        headerName: "Mcc",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 120,
      },
      {
        field: "mnc",
        headerName: "Mnc",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 120,
      },
      {
        field: "countryName",
        headerName: "Country Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "operators",
        headerName: "Operators",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 300,
      },
      {
        field: "senderIdDirectory",
        headerName: "SenderId's Directory",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "phoneNumberDirectory",
        headerName: "PhoneNumber's Directory",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "rate",
        headerName: "Rate",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "createdAt",
        headerName: "CreatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "updatedAt",
        headerName: "UpdatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "Account.displayName",
        headerName: "Created By",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "action",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
        renderCell: ({ row }) => (
          <RatePlanDropDownOptions
            // ratePlanIds={[...selectedIds, row.id]}
            ratePlanIds={_.union(selectedIds, [row.id])}
            ratePlanId={row.id}
            rateId={row.rateId}
            onRatePlanUpdate={onRatePlanUpdateOpen}
            onRatePlanDelete={onRatePlanDeleteOpen}
            selectedIds={selectedIds}
          />
        ),
      },
    ],
    [selectAll, selectedIds],
  );

  // Rows mapping.
  const rows = React.useMemo(
    () =>
      _.compact(
        _rows?.map((item, index) =>
          _successFlags.includes(item?.status)
            ? {
                id: item?.id || String.random(9),
                rateId: item?.Rate?.id,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                rateName: item?.Rate?.displayName ?? "-",
                mcc: item?.Mcc?.mcc ?? "-",
                mnc: item?.Mnc?.mnc ?? "-",
                countryName: item?.Mcc?.countryName ?? "-",
                operators: item?.Mnc?.network ?? "-",
                senderIdDirectory: item?.SenderIdDirectory?.displayName ?? "-",
                phoneNumberDirectory:
                  item?.PhoneNumberoDirectory?.displayName ?? "-",
                rate: item?.rate
                  ? `${item?.rate} ${item?.Rate?.currency}`
                  : "-",
                createdAt: Moment(item?.createdAt).format("YYYY-MM-DD"),
                updatedAt: Moment(item?.updatedAt).format("YYYY-MM-DD"),
                "Account.displayName": item?.Account?.displayName ?? "-",
              }
            : null,
        ),
      ) || [],
    [_rows, paginationModel.page, paginationModel.pageSize],
  );

  // Return component.
  return (
    <>
      <Flex className="ratePlan base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "14px"}
          bg="white"
          minH={_isCurrentViewMobile ? "130vh" : "98vh"}
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          {/* Header Section */}
          <Flex
            pb="0px"
            justify="space-between"
            overflow="hidden"
            flexWrap={{ base: "wrap", md: "wrap" }}
          >
            <Flex gap="12px">
              <Tooltip label="Create new rate plan." fontSize="sm">
                <Button
                  onClick={onRatePlanCreateOpen}
                  leftIcon={<HiPlusCircle />}
                  style={buttonStyle}
                >
                  New Rate Plan
                </Button>
              </Tooltip>
              <Flex zIndex={1000}>
                <PlanFilterOptions
                  onChange={(e) => {
                    // Conditional based toggling.
                    if ("SENDER_ID" === e) {
                      // Only show senderId based routing.
                      setOnlyShowSenderIdBasedRouting(
                        !onlyShowSenderIdBasedRouting,
                      );

                      // Disable other filters.
                      setOnlyShowPhoneNumberBasedRouting(false);
                    } else if ("PHONE_NUMBER" === e) {
                      // Only show phone number based routing.
                      setOnlyShowPhoneNumberBasedRouting(
                        !onlyShowPhoneNumberBasedRouting,
                      );

                      // Disable other filters.
                      setOnlyShowSenderIdBasedRouting(false);
                    } else {
                      // Show all.
                      setOnlyShowSenderIdBasedRouting(false);
                      setOnlyShowPhoneNumberBasedRouting(false);
                    }
                  }}
                />
              </Flex>
            </Flex>
            <Flex gap="12px">
              <FileUpload
                uploadingFile={uploadingFile}
                label="Upload RatePlan"
                onChange={(e) => {
                  // Set uploadingFile to true when file upload begins.
                  setUploadingFile(true);

                  // Promise all file uploads.
                  Promise.all(
                    Array.from(e.target.files).map((file) => {
                      // Const assignment.
                      const _FileReader = new FileReader();

                      // Event listener.
                      _FileReader.readAsBinaryString(file);
                      _FileReader.onload = () => {
                        // Local variable.
                        let i;

                        // Const assignment.
                        const _ratePlan = [];
                        const _data = _FileReader.result;
                        const _workbook = XLSX.read(_data, { type: "binary" });
                        const _sheetName = _workbook.SheetNames[0];
                        const _sheet = _workbook.Sheets[_sheetName];
                        const _parsedData = XLSX.utils.sheet_to_json(_sheet);

                        // Convert this _parsedData to array of rows.
                        for (i = 0; i < _parsedData.length; i++) {
                          // Const assignment.
                          const _rate =
                            _parsedData[i].Rate ||
                            _parsedData[i].rate ||
                            _parsedData[i]["rate*"] ||
                            _parsedData[i]["Rate*"];
                          const _mnc =
                            _parsedData[i].MNC ||
                            _parsedData[i].Mnc ||
                            _parsedData[i].mnc ||
                            _parsedData[i]["mnc*"] ||
                            _parsedData[i]["Mnc*"];
                          const _mcc =
                            _parsedData[i].MCC ||
                            _parsedData[i].Mcc ||
                            _parsedData[i].mcc ||
                            _parsedData[i]["mcc*"] ||
                            _parsedData[i]["Mcc*"];

                          // Push to _ratePlan.
                          _ratePlan.push({
                            rateId: passOn?.rateId,
                            rate: parseFloat(_rate ?? 0),
                            mcc: (_mcc ?? 0)?.toString?.(),
                            mnc: [(_mnc ?? 0)?.toString?.()],
                          });
                        }

                        // Await all ratePlan creation.
                        MutationRatePlanCreate({
                          variables: { input: _ratePlan, replaceAll: true },
                        })
                          .then((er) => {
                            // Set uploadingFile to false when upload completes.
                            setUploadingFile(false);

                            // Refetch the data.
                            toast(
                              _.first(er?.data?.RatePlanCreate)?.message ??
                                "Something is not right. try again after some time.",
                            );
                          })
                          .catch(() => {
                            // Set uploadingFile to false when upload completes.
                            setUploadingFile(false);

                            // Refetch the data.
                            toast(
                              "CSV doesn't have the right format. Please check the format and try again.",
                            );
                          });
                      };

                      // Return void 0.
                      return void 0;
                    }),
                  );
                }}
                accept=".xlsx, .xls, .csv"
                disabled={uploadingFile}
              />
              <DownloadToExcel
                cellsData={
                  0 === _rows?.length ||
                  !_.every(
                    _.pluck(_rows, "status"),
                    (i) => "READ_SUCCESSFUL" === i,
                  )
                    ? []
                    : _rows?.map((item, __index) => ({
                        "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                        "Rate Name": item?.Rate?.displayName ?? "-",
                        Mcc: item?.Mcc?.mcc ?? "-",
                        Mnc: item?.Mnc?.mnc ?? "-",
                        "Country Name": item?.Mcc?.countryName ?? "-",
                        Operators: item?.Mnc?.network ?? "-",
                        Rate: item?.rate ? item?.rate : "-",
                        CreatedAt: Moment(item.createdAt).format("YYYY-MM-DD"),
                        UpdatedAt: Moment(item.updatedAt).format("YYYY-MM-DD"),
                      }))
                }
                headersData={[
                  "S.No.",
                  "Rate Name",
                  "Mcc",
                  "Mnc",
                  "Country Name",
                  "Operators",
                  "Rate",
                  "CreatedAt",
                  "UpdatedAt",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>

          {/* Filter Section */}
          <Flex
            gap="12px"
            flexWrap="wrap"
            bg="white"
            p="10px"
            borderRadius="12px"
            border="1px solid #C5CFE8"
            alignItems="flex-start"
          >
            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mcc
              </Text>
              <MemoizedInput
                placeholder="Search by Mcc"
                className="filter searchInput"
                defaultValue={mccSearch}
                onChange={_onMccInputChange}
              />
            </Box>

            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mnc
              </Text>
              <MemoizedInput
                placeholder="Search by Mnc"
                className="filter searchInput"
                defaultValue={mncSearch}
                onChange={_onMncInputChange}
              />
            </Box>

            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Country Name
              </Text>
              <MemoizedInput
                placeholder="Search by Country"
                className="filter searchInput"
                defaultValue={countrySearch}
                onChange={_onCountryInputChange}
              />
            </Box>

            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Operators
              </Text>
              <MemoizedInput
                placeholder="Search by Operator"
                className="filter searchInput"
                defaultValue={operatorSearch}
                onChange={_onOperatorsInputChange}
              />
            </Box>
          </Flex>

          {/* MUI DataGrid */}
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={
              _.first(_QueryRatePlanRead?.data?.RatePlanRead)?._totalCount ?? 0
            }
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            loading={_QueryRatePlanRead?.loading}
          />
        </Flex>

        {/* Modals */}
        <Modal
          size="2xl"
          title="Rate Plan Create"
          isOpen={isRatePlanCreateOpen}
          onClose={onRatePlanCreateClose}
          isCentered={true}
        >
          <RatePlanUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size="2xl"
          title="Rate Plan Update"
          isOpen={isRatePlanUpdateOpen}
          onClose={onRatePlanUpdateClose}
          isCentered={true}
        >
          <RatePlanUpsert />
        </Modal>
        <Modal
          size="md"
          title="Rate Plan Delete"
          isOpen={isRatePlanDeleteOpen}
          onClose={onDeleteModalClose}
        >
          <RatePlanDelete />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {
  passOn: PropTypes.object,
};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({ passOn: __state.PassOn });

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index);
